import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { ErrorLike } from '@apollo/client';
import { useApolloClient, useQuery } from '@apollo/client/react';

import { gql } from '@graphql/__generated__';
import {
  type Activities_Bool_Exp,
  Activity_Types_Enum_Enum,
} from '@graphql/__generated__/graphql';
import { CreateListingModal } from '@modules/_shared/components/create-listing/CreateListingModal';
import type { OutcomeFollowUpAction } from '@modules/_shared/components/feed/activityOutcomeShared';
import { CallOutcomeDialogFlow } from '@modules/_shared/components/feed/CallOutcomeDialogFlow';
import { useBuyerLeadLostWorkflow } from '@modules/_shared/components/feed/hooks/useBuyerLeadLostWorkflow';
import { useLeadLostWorkflow } from '@modules/_shared/components/feed/hooks/useLeadLostWorkflow';
import { ListingActivityContextDialog } from '@modules/_shared/components/feed/ListingActivityContextDialog';
import { TaskOutcomeDialogFlow } from '@modules/_shared/components/feed/TaskOutcomeDialogFlow';
import {
  type ActivityContextValue,
  type ActivityProviderProps,
  type ActivityType,
  type ConflictState,
  type DialogState,
  type LeadResolutionState,
  type ListingActivityContext,
  type ParentContext,
  type ParentTypeOption,
  type PendingPlanActivity,
  type PipelineLeadType,
} from '@modules/_shared/components/feed/types';
import { UnifiedActivityDialog } from '@modules/_shared/components/feed/UnifiedActivityDialog';
import { GET_PENDING_ACTIVITIES } from '@modules/_shared/components/feed/utils/activityQueries';
import { VisitOutcomeDialogFlow } from '@modules/_shared/components/feed/VisitOutcomeDialogFlow';
import { LeadLostDialog } from '@modules/_shared/components/LeadLostDialog';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useUpdateLeadAgents } from '@modules/_shared/hooks/useUpdateLeadAgents';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { PendingActivityConflictDialog } from '@modules/lead-details/components/PendingActivityConflictDialog';
import { usePlanActivityConflict } from '@modules/lead-details/hooks/usePlanActivityConflict';
import { GET_LEAD_DETAILS } from '@modules/lead-details/LeadDetailsDrawer';
import type { LeadDetailsDrawer as LeadDetailsDrawerData } from '@modules/lead-details/type';
import { groupActivitiesByType } from '@modules/lead-details/utils/groupActivitiesByType';
import {
  GET_BROKERAGE_PIPELINE,
  GET_LEAD_AGENTS,
} from '@modules/leads/leadsQueries';
import { useLocale } from '@shared/hooks/locale';

const GET_FEED_LEAD_INFO = gql(/* GraphQL */ `
  query ActivityProviderLeadInfo($lead_id: uuid!) {
    leads_by_pk(id: $lead_id) {
      id
      phone_number
      lots(limit: 1) {
        id
      }
      contact {
        id
        phone_numbers(
          order_by: [{ primary: desc }, { created_at: desc }]
          limit: 1
        ) {
          id
          number
          primary
          created_at
        }
      }
    }
  }
`);

const PLANNABLE_ACTIVITY_TYPE_ENUMS: Activity_Types_Enum_Enum[] = [
  Activity_Types_Enum_Enum.Call,
  Activity_Types_Enum_Enum.Visit,
  Activity_Types_Enum_Enum.Task,
];

const isPlannableType = (
  type: ActivityType,
): type is 'call' | 'visit' | 'task' =>
  type === 'call' || type === 'visit' || type === 'task';

const DEFAULT_CONTEXT_PICKER_STATE: {
  open: boolean;
  activityType: ActivityType | null;
} = { open: false, activityType: null };

const DEFAULT_DIALOG_STATE: DialogState = {
  open: false,
  activeType: null,
  mode: 'create',
  activityId: null,
  parentType: null,
  parentId: null,
};

const parentTypeToPipeline = (
  parentType: 'Lead' | 'Lot' | 'BuyerLead' | 'User' | 'Enquiry' | null,
): PipelineLeadType => (parentType === 'Lead' ? 'sales' : 'buyer');

const PARENT_ACTIVITY_FILTER_COLUMN: Record<
  ParentTypeOption,
  keyof Activities_Bool_Exp
> = {
  Lead: 'lead_id',
  Lot: 'lot_id',
  BuyerLead: 'buyer_lead_id',
  User: 'user_id',
  Enquiry: 'enquiry_id',
};

const resolveActivityId = (
  type: 'call' | 'visit' | 'task',
  conflict: PendingPlanActivity | null,
  activeOutcome: {
    type: 'call' | 'visit' | 'task';
    activityId: string;
    initialOutcome: 'completed' | 'cancelled' | null;
  } | null,
): string | null => {
  if (conflict?.id) {
    return conflict.id;
  }

  if (activeOutcome?.type === type) {
    return activeOutcome.activityId;
  }

  return null;
};

const SCHEDULING_ACTION_MAP: Record<
  'schedule_call' | 'schedule_visit' | 'schedule_task' | 'note',
  ActivityType
> = {
  schedule_call: 'call',
  schedule_visit: 'visit',
  schedule_task: 'task',
  note: 'note',
};

export const ActivityContext = createContext<ActivityContextValue | null>(null);

export const ActivityProvider = ({
  parentType,
  parentId,
  disabled = false,
  fallbackPhoneNumber,
  EmailFormComponent,
  children,
}: ActivityProviderProps) => {
  const { me } = useAppData();
  const { t } = useLocale();
  const client = useApolloClient();

  const [dialogState, setDialogState] =
    useState<DialogState>(DEFAULT_DIALOG_STATE);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [pendingLeadResolution, setPendingLeadResolution] =
    useState<LeadResolutionState | null>(null);
  const [errorLike, setErrorLike] = useState<ErrorLike | null>(null);
  const [activeOutcome, setActiveOutcome] = useState<{
    type: 'call' | 'visit' | 'task';
    activityId: string;
    initialOutcome: 'completed' | 'cancelled' | null;
    assignedTo?: string;
    note?: string | null;
  } | null>(null);
  const [callOutcomeLoading, setCallOutcomeLoading] = useState(false);
  const [visitOutcomeLoading, setVisitOutcomeLoading] = useState(false);
  const [visitOutcomeType, setVisitOutcomeType] = useState<
    'completed' | 'cancelled' | null
  >(null);
  const [taskOutcomeLoading, setTaskOutcomeLoading] = useState(false);
  const [taskOutcomeType, setTaskOutcomeType] = useState<
    'completed' | 'cancelled' | null
  >(null);

  const [contextPicker, setContextPicker] = useState(
    DEFAULT_CONTEXT_PICKER_STATE,
  );
  const pendingContextRef = useRef<ListingActivityContext | null>(null);

  const parent: ParentContext = useMemo(
    () => (parentType && parentId ? { type: parentType, id: parentId } : null),
    [parentType, parentId],
  );

  const parentLeadId = parent?.type === 'Lead' ? parent.id : null;

  const { data: leadInfoData, error: leadInfoError } = useQuery(
    GET_FEED_LEAD_INFO,
    {
      variables: { lead_id: parentLeadId ?? '' },
      skip: !parentLeadId,
    },
  );
  const leadInfo = leadInfoData?.leads_by_pk ?? null;

  const { data: stagesData, error: stagesError } = useQuery(
    GET_BROKERAGE_PIPELINE,
    {
      skip: !parentLeadId,
    },
  );

  const [updateLeadAgents] = useUpdateLeadAgents({
    onError: setErrorLike,
    awaitRefetchQueries: true,
    refetchQueries: [GET_LEAD_DETAILS, GET_LEAD_AGENTS],
  });

  const { data: leadDetailsData, error: leadDetailsError } = useQuery(
    GET_LEAD_DETAILS,
    {
      variables: { lead_id: parentLeadId ?? '' },
      skip: !parentLeadId,
    },
  );
  const leadDetails = (leadDetailsData?.leads_by_pk ??
    null) as LeadDetailsDrawerData | null;

  const pendingActivitiesParentFilter =
    useMemo<Activities_Bool_Exp | null>(() => {
      if (!parent?.id) {
        return null;
      }
      return {
        [PARENT_ACTIVITY_FILTER_COLUMN[parent.type]]: { _eq: parent.id },
      };
    }, [parent]);

  const pendingActivitiesWhere = useMemo<Activities_Bool_Exp>(
    () => ({
      // When there is no parent (e.g. the dashboard feed) the query is skipped,
      // but a `refetchQueries` force-refetch can still run it — fall back to a
      // never-matching clause so it returns nothing rather than scanning every
      // pending activity assigned to the user.
      ...(pendingActivitiesParentFilter ?? { id: { _is_null: true } }),
      done: { _eq: false },
      activity_type: { _in: PLANNABLE_ACTIVITY_TYPE_ENUMS },
      assigned_to: { _eq: me?.id },
    }),
    [pendingActivitiesParentFilter, me?.id],
  );

  const { data: pendingActivitiesData, error: pendingActivitiesError } =
    useQuery(GET_PENDING_ACTIVITIES, {
      variables: { where: pendingActivitiesWhere },
      skip: !pendingActivitiesParentFilter || !me?.id,
    });

  // Apollo v4 removed `onError` from `useQuery`. Surface query errors through
  // the same snackbar that mutation onError callbacks feed.
  useEffect(() => {
    const queryError =
      leadInfoError ??
      stagesError ??
      leadDetailsError ??
      pendingActivitiesError;
    if (queryError) {
      setErrorLike(queryError);
    }
  }, [leadInfoError, stagesError, leadDetailsError, pendingActivitiesError]);

  const pendingActivitiesByType = useMemo(
    () => groupActivitiesByType(pendingActivitiesData?.activities ?? []),
    [pendingActivitiesData?.activities],
  );
  const {
    pendingConflict,
    callConflict,
    visitConflict,
    taskConflict,
    handlePlanActivityClick,
    handleConflictClose,
    handleConfirmCreateNew,
    handleConfirmUpdateExisting,
    setPendingConflict,
    setCallConflict,
    setVisitConflict,
    setTaskConflict,
  } = usePlanActivityConflict({
    pendingActivitiesByType,
    onPlanActivitySelect: (action, context) => {
      const chosenContext = context ?? pendingContextRef.current;
      setDialogState({
        open: true,
        activeType: action,
        mode: 'create',
        activityId: null,
        parentType: chosenContext?.parentType ?? parent?.type,
        parentId: chosenContext?.parentId ?? parent?.id,
        contactIds: chosenContext?.contactIds ?? null,
        contextLotId: chosenContext?.contextLotId ?? null,
      });
    },
  });

  const conflictState = useMemo<ConflictState>(
    () => ({
      pendingConflict,
      callConflict,
      visitConflict,
      taskConflict,
    }),
    [pendingConflict, callConflict, visitConflict, taskConflict],
  );

  const cancelActiveOutcome = useCallback(() => {
    setActiveOutcome(null);
  }, []);
  const phoneNumber =
    leadInfo?.contact?.phone_numbers[0]?.number ??
    leadInfo?.phone_number ??
    fallbackPhoneNumber ??
    null;

  const phoneActions = useMemo(
    () => ({
      canUsePhone: phoneNumber != null && !disabled,
      phoneNumber,
      openWhatsApp: () => {
        if (phoneNumber) {
          window.open(`https://wa.me/${phoneNumber}`, '_blank');
        }
      },
      openSMS: () => {
        if (phoneNumber) {
          window.open(`sms:${phoneNumber}`, '_blank');
        }
      },
    }),
    [disabled, phoneNumber],
  );

  const openDialog = useCallback(
    (
      type: ActivityType,
      mode: 'create' | 'edit' = 'create',
      activityId: string | null = null,
    ) => {
      if (mode === 'create') {
        pendingContextRef.current = null;

        if (
          parent?.type === 'Lot' &&
          (type === 'note' ||
            type === 'task' ||
            type === 'call' ||
            type === 'visit')
        ) {
          setContextPicker({ open: true, activityType: type });
          return;
        }

        if (isPlannableType(type)) {
          handlePlanActivityClick(type);
          return;
        }
      }

      setDialogState({
        open: true,
        activeType: type,
        mode,
        activityId,
        parentType: parent?.type,
        parentId: parent?.id,
      });
    },
    [handlePlanActivityClick, parent],
  );

  const openActivityWithContext = useCallback(
    async (type: ActivityType, context: ListingActivityContext) => {
      setContextPicker(DEFAULT_CONTEXT_PICKER_STATE);
      pendingContextRef.current = context;

      const assignedTo = me?.id;

      if (context.parentType === 'Lot' && isPlannableType(type)) {
        handlePlanActivityClick(type);
        return;
      }

      if (
        context.parentType === 'BuyerLead' &&
        isPlannableType(type) &&
        assignedTo
      ) {
        try {
          const { data } = await client.query({
            query: GET_PENDING_ACTIVITIES,
            variables: {
              where: {
                buyer_lead_id: { _eq: context.parentId },
                done: { _eq: false },
                activity_type: { _in: PLANNABLE_ACTIVITY_TYPE_ENUMS },
                assigned_to: { _eq: assignedTo },
              },
            },
            fetchPolicy: 'network-only',
          });

          const pending = groupActivitiesByType(data?.activities ?? [])[type];
          if (pending?.length === 1) {
            setPendingConflict({ action: type, task: pending[0], context });
            return;
          }
        } catch {
          // intentionally empty
        }
      }

      setDialogState({
        open: true,
        activeType: type,
        mode: 'create',
        activityId: null,
        parentType: context.parentType,
        parentId: context.parentId,
        contactIds: context.contactIds,
        contextLotId: context.contextLotId,
      });
    },
    [handlePlanActivityClick, client, me?.id, setPendingConflict],
  );

  const closeContextPicker = useCallback(() => {
    setContextPicker(DEFAULT_CONTEXT_PICKER_STATE);
  }, []);

  const closeDialog = useCallback(() => {
    pendingContextRef.current = null;
    setDialogState(DEFAULT_DIALOG_STATE);
  }, []);

  const startOutcomeFlow = useCallback(
    (
      type: 'call' | 'visit' | 'task',
      context: {
        activityId: string;
        initialOutcome?: 'completed' | 'cancelled' | null;
        assignedTo?: string;
        note?: string | null;
      },
    ) => {
      setActiveOutcome({
        type,
        activityId: context.activityId,
        initialOutcome: context.initialOutcome ?? null,
        assignedTo: context.assignedTo,
        note: context.note ?? null,
      });
    },
    [],
  );

  const {
    lostReasonDialog: buyerLeadLostReasonDialog,
    triggerLost: triggerBuyerLeadLost,
    triggerWon: triggerBuyerLeadWon,
  } = useBuyerLeadLostWorkflow({ setErrorLike });

  const clearAllErrors = useCallback(() => {
    setErrorLike(null);
  }, []);

  const activeOutcomeActivityId = activeOutcome?.activityId ?? null;
  const activeOutcomeType = activeOutcome?.type ?? null;

  const contextValue = useMemo<ActivityContextValue>(
    () => ({
      dialogState,
      openDialog,
      closeDialog,
      startOutcomeFlow,
      cancelOutcomeFlow: cancelActiveOutcome,
      conflictState,
      phoneActions,
      leadData: leadDetails,
      parent,
      errorLike,
      clearError: clearAllErrors,
      reportError: setErrorLike,
      emailDialogOpen,
      pendingLeadResolution,
      setPendingLeadResolution,
      openEmailDialog: () => setEmailDialogOpen(true),
      closeEmailDialog: () => setEmailDialogOpen(false),
      EmailFormComponent,
      callOutcomeLoading,
      visitOutcomeLoading,
      visitOutcomeType,
      taskOutcomeLoading,
      taskOutcomeType,
      activeOutcomeActivityId,
      activeOutcomeType,
    }),
    [
      errorLike,
      cancelActiveOutcome,
      clearAllErrors,
      closeDialog,
      conflictState,
      dialogState,
      emailDialogOpen,
      EmailFormComponent,
      pendingLeadResolution,
      leadDetails,
      openDialog,
      parent,
      phoneActions,
      setPendingLeadResolution,
      startOutcomeFlow,
      callOutcomeLoading,
      visitOutcomeLoading,
      visitOutcomeType,
      taskOutcomeLoading,
      taskOutcomeType,
      activeOutcomeActivityId,
      activeOutcomeType,
    ],
  );

  const conflictTask = callConflict ?? visitConflict ?? taskConflict;
  const inferParentFromTask = parent?.type === 'Lot' ? conflictTask : null;
  const effectiveParentType: ParentTypeOption | null = inferParentFromTask
    ? inferParentFromTask.buyerLeadId != null
      ? 'BuyerLead'
      : inferParentFromTask.leadId != null
      ? 'Lead'
      : inferParentFromTask.lotId != null
      ? 'Lot'
      : inferParentFromTask.userId != null
      ? 'User'
      : parent?.type ?? null
    : parent?.type ?? null;
  const effectiveParentId =
    (inferParentFromTask
      ? inferParentFromTask.buyerLeadId ??
        inferParentFromTask.leadId ??
        inferParentFromTask.lotId ??
        inferParentFromTask.userId
      : null) ??
    parent?.id ??
    null;

  const pipeline = parentTypeToPipeline(effectiveParentType);

  const canMarkLeadLost =
    effectiveParentType === 'Lead' || effectiveParentType === 'BuyerLead';

  const leadLostAssignedAgentId =
    pendingLeadResolution?.context.activity?.assignedTo ?? null;

  const {
    leadLostDialogState,
    showNoAgentError,
    clearLeadLostDialogState,
    clearNoAgentError,
  } = useLeadLostWorkflow({
    shouldOpen:
      pendingLeadResolution?.action === 'lead_lost' && parent?.type === 'Lead',
    parentType: parent?.type ?? null,
    leadId: parentLeadId ?? undefined,
    assignedAgentId: leadLostAssignedAgentId,
    setErrorLike,
  });

  useEffect(() => {
    if (!showNoAgentError) {
      return;
    }

    if (!errorLike) {
      setErrorLike(new Error(t('No active agent is assigned to this lead.')));
    }
    clearNoAgentError();
  }, [showNoAgentError, clearNoAgentError, errorLike, setErrorLike, t]);

  useEffect(() => {
    const resolution = pendingLeadResolution;
    if (!resolution || resolution.context.parentType !== 'BuyerLead') {
      return;
    }

    let cancelled = false;

    const executeWorkflow = async () => {
      if (cancelled) {
        return;
      }

      try {
        if (resolution.action === 'lead_lost') {
          await triggerBuyerLeadLost(resolution.context);
        } else if (resolution.action === 'lead_won') {
          await triggerBuyerLeadWon(resolution.context);
        }
      } finally {
        if (!cancelled) {
          setPendingLeadResolution(null);
        }
      }
    };

    executeWorkflow();

    return () => {
      cancelled = true;
    };
  }, [pendingLeadResolution, triggerBuyerLeadLost, triggerBuyerLeadWon]);

  // Handle lead_won for regular Lead with existing lot - just update stage
  useEffect(() => {
    const resolution = pendingLeadResolution;
    if (
      parent?.type !== 'Lead' ||
      !resolution ||
      resolution.action !== 'lead_won' ||
      (leadInfo?.lots?.length ?? 0) === 0
    ) {
      return;
    }

    const updateStage = async () => {
      const stages = stagesData?.pipelines[0]?.stages ?? [];
      const wonStage = stages.find(
        (stage): stage is (typeof stages)[number] => stage.status === 'won',
      );
      const agentId = resolution.context.activity?.assignedTo;

      if (wonStage && agentId) {
        await updateLeadAgents(
          parentLeadId ?? '',
          { stage_id: wonStage.id },
          agentId,
        );
      }

      emitFeedRefresh();
    };

    updateStage();
  }, [
    parent?.type,
    pendingLeadResolution,
    leadInfo?.lots,
    stagesData?.pipelines,
    updateLeadAgents,
    parentLeadId,
  ]);

  const hasPendingLeadResolution = Boolean(pendingLeadResolution);

  const callOutcomeOpen =
    !hasPendingLeadResolution &&
    (Boolean(callConflict) || activeOutcome?.type === 'call');
  const visitOutcomeOpen =
    !hasPendingLeadResolution &&
    (Boolean(visitConflict) || activeOutcome?.type === 'visit');
  const taskOutcomeOpen =
    !hasPendingLeadResolution &&
    (Boolean(taskConflict) || activeOutcome?.type === 'task');

  const callAutoOutcome =
    activeOutcome?.type === 'call' ? activeOutcome.initialOutcome : null;
  const visitAutoOutcome =
    activeOutcome?.type === 'visit' ? activeOutcome.initialOutcome : null;
  const taskAutoOutcome =
    activeOutcome?.type === 'task' ? activeOutcome.initialOutcome : null;

  // Compute effective activityId for each outcome dialog
  const callActivityId = resolveActivityId('call', callConflict, activeOutcome);
  const visitActivityId = resolveActivityId(
    'visit',
    visitConflict,
    activeOutcome,
  );
  const taskActivityId = resolveActivityId('task', taskConflict, activeOutcome);

  const handleFollowUpAction = useCallback(
    (action: OutcomeFollowUpAction) => {
      if (typeof action === 'string' && action in SCHEDULING_ACTION_MAP) {
        const activityType =
          SCHEDULING_ACTION_MAP[action as keyof typeof SCHEDULING_ACTION_MAP];
        setDialogState({
          open: true,
          activeType: activityType,
          mode: 'create',
          activityId: null,
          parentType: effectiveParentType,
          parentId: effectiveParentId,
        });
        setActiveOutcome(null);
        return;
      }

      if (action === 'lead_lost' || action === 'lead_won') {
        const resolutionSource: {
          type: 'call' | 'visit' | 'task';
          activity: PendingPlanActivity | null;
          activityId: string;
        } | null = callConflict
          ? {
              type: 'call',
              activity: callConflict,
              activityId: callConflict.id,
            }
          : visitConflict
          ? {
              type: 'visit',
              activity: visitConflict,
              activityId: visitConflict.id,
            }
          : taskConflict
          ? {
              type: 'task',
              activity: taskConflict,
              activityId: taskConflict.id,
            }
          : activeOutcome
          ? {
              type: activeOutcome.type,
              activity: {
                id: activeOutcome.activityId,
                assignedTo: activeOutcome.assignedTo ?? null,
              },
              activityId: activeOutcome.activityId,
            }
          : null;

        if (!resolutionSource) {
          emitFeedRefresh();
          setActiveOutcome(null);
          return;
        }

        setPendingLeadResolution({
          action,
          chain: resolutionSource.type,
          context: {
            activityId: resolutionSource.activityId,
            parentType: effectiveParentType,
            parentId: effectiveParentId,
            pipelineType: pipeline,
            activity: resolutionSource.activity,
          },
        });
        setActiveOutcome(null);
        return;
      }

      setActiveOutcome(null);
      emitFeedRefresh();
    },
    [
      activeOutcome,
      callConflict,
      effectiveParentId,
      effectiveParentType,
      pipeline,
      setPendingLeadResolution,
      taskConflict,
      visitConflict,
    ],
  );

  const handleOutcomeClose = useCallback(
    (type: 'call' | 'visit' | 'task') => {
      if (type === 'call') {
        setCallConflict(null);
      }
      if (type === 'visit') {
        setVisitConflict(null);
      }
      if (type === 'task') {
        setTaskConflict(null);
      }

      setActiveOutcome(null);
    },
    [setCallConflict, setVisitConflict, setTaskConflict],
  );

  const activeDialogParent = useMemo<
    Exclude<ParentContext, null> | undefined
  >(() => {
    if (dialogState.parentType && dialogState.parentId) {
      return {
        type: dialogState.parentType,
        id: dialogState.parentId,
      } as Exclude<ParentContext, null>;
    }

    if (parent?.type && parent?.id) {
      return {
        type: parent.type,
        id: parent.id,
      } as Exclude<ParentContext, null>;
    }

    return undefined;
  }, [dialogState.parentId, dialogState.parentType, parent]);

  return (
    <ActivityContext.Provider value={contextValue}>
      {children}

      <UnifiedActivityDialog
        open={dialogState.open && dialogState.activeType != null}
        onClose={closeDialog}
        activityType={dialogState.activeType ?? undefined}
        mode={dialogState.mode}
        activityId={dialogState.activityId}
        parent={activeDialogParent}
        leadData={leadDetails}
        initialContactIds={dialogState.contactIds}
        contextLotId={dialogState.contextLotId}
        onCreate={closeDialog}
      />

      {parent?.type === 'Lot' && parent.id && (
        <ListingActivityContextDialog
          open={contextPicker.open}
          lotId={parent.id}
          onClose={closeContextPicker}
          onSelect={context => {
            if (contextPicker.activityType) {
              openActivityWithContext(contextPicker.activityType, context);
            }
          }}
        />
      )}

      <PendingActivityConflictDialog
        open={!!conflictState.pendingConflict}
        actionLabel={
          {
            call: t('call'),
            visit: t('visit'),
            task: t('task'),
          }[conflictState.pendingConflict?.action ?? 'call'] ?? ''
        }
        parentType={
          conflictState.pendingConflict?.context?.parentType ??
          parent?.type ??
          null
        }
        onClose={handleConflictClose}
        onConfirmCreateNew={handleConfirmCreateNew}
        onConfirmUpdateExisting={handleConfirmUpdateExisting}
      />

      <CallOutcomeDialogFlow
        open={callOutcomeOpen}
        activity={callConflict ?? null}
        activityId={callActivityId}
        onClose={() => {
          handleOutcomeClose('call');
          setCallOutcomeLoading(false);
        }}
        onFollowUpActionSelect={handleFollowUpAction}
        canMarkLeadLost={canMarkLeadLost}
        autoOutcome={callAutoOutcome}
        parentType={effectiveParentType}
        pipelineType={pipeline}
        onLoadingChange={setCallOutcomeLoading}
      />

      <VisitOutcomeDialogFlow
        open={visitOutcomeOpen}
        activity={visitConflict ?? null}
        activityId={visitActivityId}
        onClose={() => {
          handleOutcomeClose('visit');
          setVisitOutcomeLoading(false);
        }}
        onFollowUpActionSelect={handleFollowUpAction}
        canMarkLeadLost={canMarkLeadLost}
        onCancel={
          activeOutcome?.type === 'visit' ? cancelActiveOutcome : undefined
        }
        autoOutcome={visitAutoOutcome}
        parentType={effectiveParentType}
        pipelineType={pipeline}
        onLoadingChange={(loading, outcomeType) => {
          setVisitOutcomeLoading(loading);
          setVisitOutcomeType(outcomeType);
        }}
      />

      <TaskOutcomeDialogFlow
        open={taskOutcomeOpen}
        activity={
          taskConflict ??
          (activeOutcome?.note
            ? { id: activeOutcome.activityId, note: activeOutcome.note }
            : null) ??
          null
        }
        activityId={taskActivityId}
        onClose={() => {
          handleOutcomeClose('task');
          setTaskOutcomeLoading(false);
          setTaskOutcomeType(null);
        }}
        onFollowUpActionSelect={handleFollowUpAction}
        canMarkLeadLost={canMarkLeadLost}
        autoOutcome={taskAutoOutcome}
        parentType={effectiveParentType}
        pipelineType={pipeline}
        onLoadingChange={(loading, outcomeType) => {
          setTaskOutcomeLoading(loading);
          setTaskOutcomeType(outcomeType);
        }}
      />

      <LeadLostDialog
        open={
          pendingLeadResolution?.action === 'lead_lost' &&
          pendingLeadResolution.context.parentType === 'Lead' &&
          !!leadLostDialogState
        }
        leadId={leadLostDialogState?.leadId ?? undefined}
        pipelineId={leadLostDialogState?.pipelineId ?? null}
        agentId={leadLostDialogState?.agentId ?? null}
        currentStageId={leadLostDialogState?.currentStageId ?? null}
        onClose={() => {
          setPendingLeadResolution(null);
          clearLeadLostDialogState();
        }}
        onStageUpdated={() => {
          emitFeedRefresh();
          setPendingLeadResolution(null);
          clearLeadLostDialogState();
        }}
      />

      {buyerLeadLostReasonDialog}

      <CreateListingModal
        open={
          pendingLeadResolution?.action === 'lead_won' &&
          pendingLeadResolution.context.parentType === 'Lead' &&
          (leadInfo?.lots?.length ?? 0) === 0
        }
        title={t('Lead won')}
        fromLeadId={pendingLeadResolution?.context.parentId ?? ''}
        brokerId={
          pendingLeadResolution?.context.activity?.assignedTo ?? undefined
        }
        onClose={() => {
          setPendingLeadResolution(null);
        }}
        onListingCreated={async () => {
          emitFeedRefresh();
          setPendingLeadResolution(null);
        }}
      />

      <MutationErrorSnackbar error={errorLike} onClose={clearAllErrors} />
    </ActivityContext.Provider>
  );
};

export const useActivityContext = (): ActivityContextValue => {
  const context = useContext(ActivityContext);

  if (!context) {
    throw new Error(
      'useActivityContext must be used within an ActivityProvider',
    );
  }

  return context;
};
